import AsyncStorage from '../async-storage-chunker';
import {
  BatchTagPrintingParams,
  CreditRequestDataParams,
  MinimalVariantPackParams,
  OFFLINE_DATA_KEY_PREFIX,
  OfflineData,
  OfflineParams,
  OrderConstraintsParams,
  OrderContextParams,
  OrderGuideParams,
  OrderGuidePriceEditDisplayInfoParams,
  QueuedOrdersParams,
  UserContextParams,
  VariantPackInfoParams,
  VisibleVariantPackIDsParams,
  VisibleVariantPacksParams,
} from './types';

export const getBusinessUUIDsFromOfflineOrderContextCacheKey = (
  key: string,
) => {
  const keyParts = key.split('_');
  const supplierUUID = keyParts[1];
  const restaurantUUID = keyParts[2];
  return { restaurantUUID, supplierUUID };
};

export const generateChatVariantPacksPriceEditMapKey = ({
  restaurantUUID,
  supplierUUID,
}: {
  restaurantUUID: string;
  supplierUUID: string;
}) => `${supplierUUID}_${restaurantUUID}}`;

export const getBusinessUUIDsFromChatVariantPacksPriceEditMapKey = (
  key: string,
) => {
  const keyParts = key.split('_');
  const supplierUUID = keyParts[0];
  const restaurantUUID = keyParts[1];
  return { restaurantUUID, supplierUUID };
};

export const getOfflineKey = <T extends OfflineData>(
  offlineData: T,
  params?: OfflineParams[T],
): string => {
  let key = '';
  switch (offlineData) {
    case OfflineData.AuthToken:
      key = 'AuthToken';
      break;
    case OfflineData.BackgroundSync:
      key = 'BackgroundSync';
      break;
    case OfflineData.BatchTagPrinting:
      const batchTagPrintParams = params as BatchTagPrintingParams;
      key = `BatchTagPrinting_${batchTagPrintParams.supplierUUID}_${batchTagPrintParams.restaurantUUID}`;
      break;
    case OfflineData.CreditRequestData:
      const creditRequestDataParams = params as CreditRequestDataParams;
      key = `CreditRequestData_${creditRequestDataParams.supplierUUID}_${creditRequestDataParams.restaurantUUID}`;
      break;
    case OfflineData.DataStatus:
      key = 'DataStatus';
      break;
    case OfflineData.OrderConstraints:
      const orderConstraintsParams = params as OrderConstraintsParams;
      key = `OrderConstraints_${orderConstraintsParams.supplierUUID}_${orderConstraintsParams.restaurantUUID}`;
      break;
    case OfflineData.OrderContext:
      const orderContextParams = params as OrderContextParams;
      key = `OrderContext_${orderContextParams.supplierUUID}_${orderContextParams.restaurantUUID}`;
      break;
    case OfflineData.OrderGuide:
      const orderGuideParams = params as OrderGuideParams;
      key = `OrderGuide_${orderGuideParams.supplierUUID}_${orderGuideParams.restaurantUUID}`;
      break;
    case OfflineData.OrderGuidePriceEditDisplayInfo:
      const orderGuidePriceEditDisplayInfoParams =
        params as OrderGuidePriceEditDisplayInfoParams;
      key = `OrderGuidePriceEditDisplayInfo_${orderGuidePriceEditDisplayInfoParams.supplierUUID}_${orderGuidePriceEditDisplayInfoParams.restaurantUUID}`;
      break;
    case OfflineData.PriceEdits:
      key = 'PriceEdits';
      break;
    case OfflineData.QueuedOrders:
      const queuedOrdersParams = params as QueuedOrdersParams;
      key = `QueuedOrders_${queuedOrdersParams.supplierUUID}_${queuedOrdersParams.restaurantUUID}`;
      break;
    case OfflineData.RestaurantsWithQueuedOrders:
      key = 'RestaurantsWithQueuedOrders';
      break;
    case OfflineData.UserBackgroundSync:
      key = 'UserBackgroundSync';
      break;
    case OfflineData.UserContext:
      const userContextParams = params as UserContextParams;
      key = `UserContext_${userContextParams.userUUID}`;
      break;
    case OfflineData.VariantPackInfo:
      const variantPackInfoParams = params as VariantPackInfoParams;
      key = `VariantPackInfo_${variantPackInfoParams.supplierUUID}_${variantPackInfoParams.restaurantUUID}`;
      break;
    case OfflineData.MinimalVariantPack:
      const minimalVariantPackParams = params as MinimalVariantPackParams;
      key = `MinimalVariantPack_${minimalVariantPackParams.supplierUUID}`;
      break;
    case OfflineData.VisibleVariantPacks:
      const visibleVariantPacksParams = params as VisibleVariantPacksParams;
      key = `VisibleVariantPacks_${visibleVariantPacksParams.supplierUUID}_${visibleVariantPacksParams.restaurantUUID}`;
      break;
    case OfflineData.VisibleVariantPackIDs:
      const visibleVariantPackIdsParams = params as VisibleVariantPackIDsParams;
      key = `VisibleVariantPackIDs_${visibleVariantPackIdsParams.supplierUUID}_${visibleVariantPackIdsParams.restaurantUUID}`;
      break;
    default:
      throw new Error('Invalid offline data type');
  }
  return `${OFFLINE_DATA_KEY_PREFIX}:${key}`;
};

export const clearOfflineAsyncStorage = async () => {
  const keys = await AsyncStorage.getAllKeys();
  const pepperKeys = keys.filter((key) =>
    key.startsWith(`${OFFLINE_DATA_KEY_PREFIX}:`),
  );
  await AsyncStorage.multiRemove(pepperKeys);
};
